import { useState, useMemo, useEffect } from 'react';
import { fmtPrice } from '../utils/format';
import { rsiState } from '../utils/indicators';
import { TF, useSymbolRSI } from '../utils/rsi';
import { fetchLinearTickers, resolveBybitSymbol } from '../utils/bybit';
import InfoTooltip from './InfoTooltip';
import SourceDot, { mergeMeta } from './SourceDot';
import DataSourceDown from './DataSourceDown';

/* Real RSI14 on four Bybit timeframes for one coin. Every row comes from
   closed candles only; a timeframe without enough data shows "—", never an
   estimate. Labels describe the state, they are not buy/sell instructions. */
const INTERVALS = ['15', '60', '240', 'D'];

function candleTime(ms, interval) {
  const d = new Date(ms);
  return interval === 'D'
    ? d.toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' })
    : d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
}

export default function MTFAnalysis({ coins, selectedCoinId }) {
  const [coinId, setCoinId] = useState(() => {
    try { return localStorage.getItem('mtfCoinId') ?? selectedCoinId ?? coins[0]?.id ?? ''; }
    catch { return selectedCoinId ?? coins[0]?.id ?? ''; }
  });
  const [symbol, setSymbol] = useState(undefined); // undefined = resolving, null = no perp
  const [tickersFailed, setTickersFailed] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (selectedCoinId && !localStorage.getItem('mtfCoinId')) {
      setCoinId(selectedCoinId);
    }
  }, [selectedCoinId]);

  useEffect(() => {
    if (coinId) localStorage.setItem('mtfCoinId', coinId);
  }, [coinId]);

  const coin = useMemo(() => coins.find(c => c.id === coinId) ?? coins[0], [coins, coinId]);

  useEffect(() => {
    if (!coin) return;
    let alive = true;
    setSymbol(undefined);
    setTickersFailed(false);
    fetchLinearTickers(5 * 60 * 1000)
      .then(map => { if (alive) setSymbol(resolveBybitSymbol(coin.symbol, coin.current_price, map)); })
      // Bybit down ≠ "no perpetual": keep the symbol unknown and say so.
      .catch(() => { if (alive) setTickersFailed(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coin?.id, retryNonce]);

  const rsiRows = useSymbolRSI(symbol || null, INTERVALS, retryNonce);
  const rowsFailed = INTERVALS.filter(iv => rsiRows[iv]?.reason === 'error').length;
  const retry = () => setRetryNonce(n => n + 1);

  const summary = useMemo(() => {
    const vals = INTERVALS.map(iv => rsiRows[iv]?.rsi).filter(v => v != null);
    if (!vals.length) return null;
    const os = vals.filter(v => v < 30).length;
    const ob = vals.filter(v => v > 70).length;
    return { n: vals.length, os, ob, neutral: vals.length - os - ob };
  }, [rsiRows]);

  const meta = mergeMeta(...INTERVALS.map(iv => rsiRows[iv] && { at: rsiRows[iv].fetchedAt, stale: rsiRows[iv].stale }));

  if (!coin) return null;

  return (
    <div className="mtf-wrap">
      <div className="mtf-header">
        <span className="mtf-title">TÖBB IDŐSÍKOS RSI <InfoTooltip text="Ugyanannak a coinnak a valódi RSI-je (14, Wilder) négy időtávon: 15 perc, 1 óra, 4 óra és napi, a Bybit perpetual lezárt gyertyáiból. Ha minden időtávon hasonló az állapot, az összhang; ha ellentmondanak, a piac bizonytalanabb. Leíró adat, nem vételi vagy eladási jelzés." /><SourceDot source={`Bybit ${symbol || ''} perpetual, lezárt gyertyák`} meta={meta} /></span>
        <select
          className="mtf-coin-select"
          value={coinId}
          onChange={e => setCoinId(e.target.value)}
        >
          {coins.map(c => (
            <option key={c.id} value={c.id}>{c.symbol.toUpperCase()} — {c.name}</option>
          ))}
        </select>
      </div>

      {(tickersFailed || rowsFailed > 0) && <DataSourceDown onRetry={retry} />}
      {symbol === null ? (
        <div className="sig-empty">Ehhez a coinhoz nincs Bybit USDT perpetual, ezért RSI nem számolható.</div>
      ) : (
        <div className="mtf-table-wrap">
          <table className="mtf-table">
            <thead>
              <tr>
                <th className="mtf-th">Időkeret</th>
                <th className="mtf-th right">RSI 14</th>
                <th className="mtf-th">Állapot</th>
                <th className="mtf-th">Ár vs. EMA50 <InfoTooltip text="Az utolsó lezárt gyertya záróára az 50 periódusú exponenciális mozgóátlag felett vagy alatt van-e, ugyanazon az időtávon." /></th>
                <th className="mtf-th right">Utolsó lezárt gyertya</th>
              </tr>
            </thead>
            <tbody>
              {INTERVALS.map(iv => {
                const r = rsiRows[iv];
                const st = rsiState(r?.rsi ?? null);
                return (
                  <tr key={iv} className="mtf-row">
                    <td className="mtf-td mtf-tf-label">{TF[iv].label}</td>
                    <td className="mtf-td right">
                      <span className="mtf-rsi-val" style={{ color: st.color }}>
                        {r?.rsi != null ? r.rsi.toFixed(1) : r ? (r.reason === 'short' ? '— kevés adat' : '—') : tickersFailed ? '—' : '…'}
                      </span>
                    </td>
                    <td className="mtf-td">
                      {r?.rsi != null
                        ? <span className="mtf-badge" style={{ color: st.color, borderColor: 'currentColor' }}>{st.text}</span>
                        : <span className="ind-na">—</span>}
                    </td>
                    <td className="mtf-td">
                      {r?.aboveEma50 == null ? '—'
                        : r.aboveEma50
                          ? <span className="positive">Felette</span>
                          : <span className="negative">Alatta</span>}
                    </td>
                    <td className="mtf-td right" style={{ color: 'var(--text3)' }}>
                      {r?.candleStart ? `${candleTime(r.candleStart, iv)} · ${fmtPrice(r.lastClose)}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {summary && (
        <div className="mtf-bias-bar">
          <span className="mtf-bias-label">
            {summary.n} idősíkból: {summary.os} túladott · {summary.ob} túlvett · {summary.neutral} semleges
          </span>
        </div>
      )}
    </div>
  );
}
